import { useEffect, useMemo } from "react";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import { defineMessages, useIntl } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { IconChevronsRight } from "@tabler/icons-react";
import { castPeriod, castPrice, isotizeDate } from "@ct-react/core";
import { LocaleLink } from "@ct-react/locale";
import { calculateCartPaymentAmount } from "@ct-react/calendar";
import { AnyCartItem, DownPayment, RentalAccommodationCartItem } from "@shared/graphql";
import { ArticleType } from "@shared/url";
import { getEnumKeyByValue } from "../../tools/enums";
import { CART_ITEM_DETAIL_GQL_DATA, FULL_CART_GQL_DATA, useCheckoutContext } from "./outlet";
import CartItem from "../../components/cart/item";
import CartPrice from "../../components/cart/price";
import "./common.scss";
import "./cart.scss";

const transDefs = defineMessages({
  emptyTitle: { id: "cart.empty.title", defaultMessage: "Vous n'avez encore rien trouvé ?" },
  emptyLink: { id: "cart.empty.link", defaultMessage: "Parcourez tous les hébergements disponibles en suivant ce lien." }
});

export const CartPage = () => {

  const intl = useIntl();
  const [ { step: { index: stepIndex }, sessionId, userId } ,dispatch ] = useCheckoutContext();

  const { data, loading } = useQuery<any>(FULL_CART_GQL_DATA, { variables: { sessionId, userId } });
  const [ fetchArticleData ] = useLazyQuery<any>(CART_ITEM_DETAIL_GQL_DATA);

  useEffect(() => {
    const newIndex = Math.min((data?.cart?.items?.length || 0), 1) as 0 | 1;
    (newIndex !== stepIndex) && dispatch({
      type: "change.index",
      payload: { index: newIndex, valid: newIndex > 0, ...(newIndex === 0) && { maskActions: true } }
    });
    if (!data?.cart?.items || data.cart.items.length === 0)
      return;

    const castedPrice = castPrice(data.cart.fullPrice.amount);
    Promise.all(data.cart.items
             .map(({ articleId}: RentalAccommodationCartItem) =>
               fetchArticleData({ variables: { articleId } })
                 .then(({ data: { detail: { __typename, ...detail } } }) => [ articleId, detail ])))
      .then(res => Object.fromEntries(res))
      .then(complement => ({
        article_type: getEnumKeyByValue(ArticleType, ArticleType.RENTAL)?.toLowerCase(),
        value: castedPrice.amount,
        currency: castedPrice.currency,
        items: data.cart.items.map((item: RentalAccommodationCartItem) => {
          const castedItemPrice = castPrice(item.price.amount);
          const castedPeriod = castPeriod(item.period);
          return {
            item_id: item.articleId,
            item_name: complement[item.articleId].technicalName,
            item_period_start: isotizeDate(castedPeriod.start),
            item_period_end: isotizeDate(castedPeriod.end),
            price: castedItemPrice.amount,
            quantity: 1
          }
        })
      }))
      .then(payload => dispatch({ type: "save.items.data", payload }));
  }, [ data ]);

  // calculate the down payment amount for cart with multiple items
  const downPayment = useMemo<DownPayment | undefined>(() => {
    if (!data?.cart || data.cart.items.length <= 1) return undefined;
    const configs = data.cart.items.map((item: AnyCartItem) => item.downPayment);
    const rate = configs.map((c: DownPayment) => c.rate).reduce((acc: number, cur: number) => Math.min(acc, cur), 1);
    const providerCharge = calculateCartPaymentAmount(data.cart.items, "full").detailed.providerCharge;
    return { rate, providerCharge };
  }, [ data ]);

  if (loading)
    return (<Skeleton className="cart-skeleton" />);

  if (!data?.cart || data.cart.items.length === 0)
    return (
      <div className="cart-item-wrap empty boxed">
        <div className="title">
          {intl.formatMessage(transDefs.emptyTitle)}
        </div>
        <div className="redirect">
          {intl.formatMessage(transDefs.emptyLink)}
          <LocaleLink to="/rentals/short">
            <IconChevronsRight className="r-icon" />
          </LocaleLink>
        </div>
      </div>);

  return (
    <>
      {data.cart.items.map((item: AnyCartItem, index: number) => (
        <CartItem key={index} className="cart-item-wrap"
                  data={item} />)
      )}
      {data.cart.items.length > 1 &&
        <div className="one-cart-item cart-item-wrap">
          <div className="two-col">
            <div className="one-item-resume" />
            <div className="one-item-price">
              <CartPrice data={{ price: data.cart.fullPrice, downPayment }} />
            </div>
          </div>
        </div>
      }
    </>);

};

export default CartPage;
